import React, { useEffect } from 'react';
import omit from 'lodash/omit';
import {
  trackNavigatorStarted,
  trackExperimentViewed,
  trackRentersNavigatorStarted,
} from '~/analytics';
import ApplicationQuery from '~/graphql/queries/Application';
import MetadataQuery from '~/graphql/queries/Metadata';
import { trackPerformanceEvent, APP_TIME_TO_INITIAL_RENDER } from '~/util/performanceTrack';
import QueryLoader from '~/components/Loaders/QueryLoader';
import { Props } from '~/types/app';
import connect from './selectors';
import ScreenSizeProvider from '~/contexts/ScreenSize';
import isRedirectedFromAuto from '~/routes/util/isRedirectedFromAuto';
import { RENTERS_URL } from '~/routes/constants';
import { useGetApplicationData } from './hooks/useGetApplicationData';

export const App = (props: Props) => {
  const applicationData = useGetApplicationData();

  useEffect(() => {
    const {
      flow,
      history: { location },
    } = props;
    const analyticsFlow = isRedirectedFromAuto(location) ? 'auto' : flow;
    const product = analyticsFlow === 'homeowners' ? 'home' : 'auto';

    trackExperimentViewed({ flow: analyticsFlow });

    if (location.pathname.includes(RENTERS_URL)) {
      trackRentersNavigatorStarted();
    } else {
      trackNavigatorStarted(product, { flow: analyticsFlow });
    }

    trackPerformanceEvent(APP_TIME_TO_INITIAL_RENDER);
  }, []);

  const { children } = props;

  return (
    <ScreenSizeProvider>
      <ApplicationQuery applicationId={applicationData.applicationId} {...props}>
        {(queryProps) => (
          <MetadataQuery>
            {(metadataProps) => {
              const loading = queryProps.loading || metadataProps.loading;

              if (loading) {
                return <QueryLoader />;
              }

              return children(
                omit(
                  {
                    ...props,
                    ...queryProps,
                    app: applicationData,
                    metadata: metadataProps.metadata,
                  },
                  'children'
                )
              );
            }}
          </MetadataQuery>
        )}
      </ApplicationQuery>
    </ScreenSizeProvider>
  );
};

export default connect(App);
